import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Button,
  Flex,
  Icon,
  Text,
} from "@chakra-ui/react";
import { t } from "i18next";
import { useEffect, useMemo, useState } from "react";
import { FaPlus } from "react-icons/fa";
import { BseQuestionDto, BseQuestionStatus, SourcingEventSupplierView } from "../../../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../../../common/auth/useLoggedInWithOrgContextState";
import { displayDateWithTime } from "../../../../../common/formatting/displayDateWithTime";
import { useQueryParams } from "../../../../../common/useQueryParams";
import { useNegotiation } from "../NegotiationContext";
import { NewQuestionModal } from "./NewQuestionModal";
import { QuestionModal } from "./QuestionModal";
import { QuestionsTable } from "./QuestionsTable";
interface Props {
  data: SourcingEventSupplierView;
  addComment: (comment: string, questionId: string, negotiationRoundId?: string) => Promise<void>;
  createNewQuestion: (values: {
    subject: string;
    content: string;
    announcementId: string | null;
  }) => Promise<SourcingEventSupplierView>;
}

export const Questions = ({ data, addComment, createNewQuestion }: Props) => {
  const [questionId, setQuestionId] = useState<string>();
  const [newQuestion, setNewQuestion] = useState(false);
  const params = useQueryParams();
  const questionParam = params.get("question");
  const { currentRound } = useNegotiation();
  const authState = useLoggedInWithOrgContextState();

  const selectedQuestion: { question: BseQuestionDto; negotiationRoundId?: string } | undefined = useMemo(() => {
    const originalEventQuestion = data.questions.find((q) => q.id === questionId);
    if (originalEventQuestion) {
      return { question: originalEventQuestion, negotiationRoundId: undefined };
    }
    const negotiationRound = data.negotiationRounds?.find((r) => r.questions.find((q) => q.id === questionId));
    if (negotiationRound) {
      return {
        question: negotiationRound.questions.find((q) => q.id === questionId)!,
        negotiationRoundId: negotiationRound.id,
      };
    }
    return undefined;
  }, [data.questions, data.negotiationRounds, questionId]);
  const getInfoSentence = (q: BseQuestionDto): string =>
    q.askedByOrgName === authState.selectedOrg.name && q.askedByPerson.id === authState.me.id
      ? `${t("Asked to")} ${q.askedToOrganization} ${t("by you")}, ${displayDateWithTime(q.askedAt)}.`
      : `${t("Asked by")} ${q.askedByPerson.firstName} ${t("in")} ${q.askedByOrgName} ${t("to")} ${q.askedToOrganization}, ${displayDateWithTime(q.askedAt)}.`;

  useEffect(() => {
    if (questionParam) setQuestionId(questionParam);
  }, [questionParam]);

  return (
    <>
      {selectedQuestion && (
        <QuestionModal
          canAddComments={true}
          addComment={async (values: { comment: string }) =>
            addComment(values.comment, selectedQuestion.question.id, selectedQuestion.negotiationRoundId)
          }
          isOpen={true}
          close={() => {
            setQuestionId(undefined);
            params.clearSelectedQuestion();
          }}
          question={selectedQuestion.question}
          addCommentDisabledReason={null}
        />
      )}
      {newQuestion && (
        <NewQuestionModal
          isOpen={newQuestion}
          close={() => setNewQuestion(false)}
          createNewQuestion={createNewQuestion}
        />
      )}
      <Flex flexDirection="column" height="full" overflow="hidden">
        {(currentRound ? currentRound.canAskQuestions : data.canAskQuestions) && (
          <Flex width="full" pt="5" pb="8">
            <Button
              leftIcon={<Icon as={FaPlus} w="15px" h="15px" />}
              colorScheme={"blue"}
              variant="outline"
              size={"sm"}
              onClick={() => setNewQuestion(true)}
            >
              {t("New dialog")}
            </Button>
          </Flex>
        )}
        {data.negotiationRounds?.length ? (
          <Accordion allowMultiple reduceMotion defaultIndex={[0, ...data.negotiationRounds.map((_, idx) => idx + 1)]}>
            {data.negotiationRounds
              .slice()
              .sort((a, b) => b.roundNumber - a.roundNumber)
              .map((round) => (
                <AccordionItem
                  key={round.id}
                  mb="2"
                  rounded="md"
                  bg="smBackground"
                  boxShadow="sm"
                  border="1px solid"
                  borderColor="smBorder"
                >
                  {({ isExpanded }) => (
                    <>
                      <h2>
                        <AccordionButton
                          py="2.5"
                          px="3"
                          _hover={{ bg: "smBackgroundSecondary" }}
                          _expanded={{ bg: "smBackgroundSecondary" }}
                          roundedTop="md"
                        >
                          <Flex as="span" flex="1" textAlign="left" align="center" gap={2}>
                            <Text pt="1">
                              {t("Negotiation round")} {round.roundNumber}
                            </Text>
                            {!isExpanded ? (
                              <>
                                {round.questions.filter(
                                  (q: BseQuestionDto) => q.status === BseQuestionStatus.Unresolved
                                ).length > 0 ? (
                                  <Badge colorScheme="red" ml={2} rounded="md">
                                    {
                                      round.questions.filter(
                                        (q: BseQuestionDto) => q.status === BseQuestionStatus.Unresolved
                                      ).length
                                    }{" "}
                                    {t("Unresolved")}
                                  </Badge>
                                ) : null}
                                {round.questions.filter((q: BseQuestionDto) => q.status === BseQuestionStatus.Resolved)
                                  .length > 0 ? (
                                  <Badge colorScheme="green" ml={1} rounded="md">
                                    {
                                      round.questions.filter(
                                        (q: BseQuestionDto) => q.status === BseQuestionStatus.Resolved
                                      ).length
                                    }{" "}
                                    {t("Resolved")}
                                  </Badge>
                                ) : null}
                              </>
                            ) : null}
                          </Flex>
                          <AccordionIcon color="smMuted" />
                        </AccordionButton>
                      </h2>
                      <AccordionPanel pb={4} px="0" roundedBottom="md">
                        <QuestionsTable
                          items={round.questions}
                          getInfoSentence={getInfoSentence}
                          onRowClick={(id: string) => {
                            setQuestionId(id);
                            params.setSelectedQuestion(id);
                          }}
                          emptyText={t("You have not asked any questions")}
                        />
                      </AccordionPanel>
                    </>
                  )}
                </AccordionItem>
              ))}
            <AccordionItem
              key={data.id}
              mb="2"
              rounded="md"
              bg="smBackground"
              boxShadow="sm"
              border="1px solid"
              borderColor="smBorder"
            >
              {({ isExpanded }) => (
                <>
                  <h2>
                    <AccordionButton
                      py="2.5"
                      px="3"
                      _hover={{ bg: "smBackgroundSecondary" }}
                      _expanded={{ bg: "smBackgroundSecondary" }}
                      roundedTop="md"
                    >
                      <Flex as="span" flex="1" textAlign="left" align="center" gap={2}>
                        <Text pt="1">{data.title}</Text>
                        {!isExpanded ? (
                          <>
                            {data.questions.filter((q: BseQuestionDto) => q.status === BseQuestionStatus.Unresolved)
                              .length > 0 ? (
                              <Badge colorScheme="red" ml={2} rounded="md">
                                {
                                  data.questions.filter(
                                    (q: BseQuestionDto) => q.status === BseQuestionStatus.Unresolved
                                  ).length
                                }{" "}
                                {t("Unresolved")}
                              </Badge>
                            ) : null}
                            {data.questions.filter((q: BseQuestionDto) => q.status === BseQuestionStatus.Resolved)
                              .length > 0 ? (
                              <Badge colorScheme="green" ml={1} rounded="md">
                                {
                                  data.questions.filter((q: BseQuestionDto) => q.status === BseQuestionStatus.Resolved)
                                    .length
                                }{" "}
                                {t("Resolved")}
                              </Badge>
                            ) : null}
                          </>
                        ) : null}
                      </Flex>
                      <AccordionIcon color="smMuted" />
                    </AccordionButton>
                  </h2>
                  <AccordionPanel pb={4} px="0" roundedBottom="md">
                    <QuestionsTable
                      items={data.questions}
                      getInfoSentence={getInfoSentence}
                      onRowClick={(id: string) => {
                        setQuestionId(id);
                        params.setSelectedQuestion(id);
                      }}
                      emptyText={t("You have not asked any questions")}
                    />
                  </AccordionPanel>
                </>
              )}
            </AccordionItem>
          </Accordion>
        ) : (
          <QuestionsTable
            items={data.questions}
            bordered={true}
            getInfoSentence={getInfoSentence}
            onRowClick={(id: string) => {
              setQuestionId(id);
              params.setSelectedQuestion(id);
            }}
            emptyText={t("You have not asked any questions")}
          />
        )}
      </Flex>
    </>
  );
};
